/**
 * useOccupancyRefresh — after an org saga mutation commits, immediately re-read
 * the affected seats' OCCUPANCY (incumbent / status / reporting line) from F&O
 * so the drawer's projection is fresh NOW instead of waiting for the nightly
 * sync. Fires exactly once, when `active` first flips true.
 *
 * Best-effort by contract: on any failure the projection still self-heals on the
 * nightly sync, so callers fall back to the honest "after the next sync" copy.
 * `settle()` lets a caller await the in-flight refresh before it refetches, so
 * the refetch observes the updated projection.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import organizationService from '@/services/api/organizationService';

export type OccupancyRefreshState = 'idle' | 'refreshing' | 'ok' | 'error';

export function useOccupancyRefresh(active: boolean, positionCodes: string[]) {
  const [state, setState] = useState<OccupancyRefreshState>('idle');
  const promiseRef = useRef<Promise<void> | null>(null);
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Join to a primitive so a new-array-every-render identity doesn't matter.
  const codesKey = positionCodes
    .map((c) => (c || '').trim())
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    if (!active || startedRef.current) return;
    const codes = codesKey ? codesKey.split(',') : [];
    if (codes.length === 0) return;
    startedRef.current = true;
    setState('refreshing');
    promiseRef.current = organizationService
      .refreshOccupancy(codes)
      .then(() => {
        if (mountedRef.current) setState('ok');
      })
      .catch(() => {
        if (mountedRef.current) setState('error');
      });
  }, [active, codesKey]);

  /** Await the in-flight refresh (resolves immediately if none / already done). */
  const settle = useCallback(async () => {
    if (promiseRef.current) {
      try {
        await promiseRef.current;
      } catch {
        /* failure already reflected in `state` */
      }
    }
  }, []);

  return { state, settle };
}

export default useOccupancyRefresh;
